import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, Search } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

function AdminStakeMarketDetail() {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedDate = searchParams.get("date") || getIndiaDateValue();
  const [session, setSession] = useState("ALL");
  const [marketDetail, setMarketDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedNumberKey, setExpandedNumberKey] = useState("");

  const loadDetail = useCallback(async () => {
    try {
      setErrorMessage("");
      const res = await API.get(`/admin/stake-analysis/market/${marketId}?date=${selectedDate}&session=${session}`);
      setMarketDetail(res.data || null);
    } catch (err) {
      console.log(err);
      setMarketDetail(null);
      setErrorMessage(err?.response?.data?.message || "Unable to load stake detail right now.");
    }
  }, [marketId, selectedDate, session]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const filteredGroups = useMemo(() => {
    if (!marketDetail?.groups) return [];
    const term = search.trim().toLowerCase();
    const baseGroups = [...marketDetail.groups].sort((a, b) => Number(b.stake || 0) - Number(a.stake || 0));
    if (!term) return baseGroups;
    return baseGroups.map((group) => ({
      ...group,
      numbers: group.numbers.filter((item) => String(item.number).toLowerCase().includes(term)),
    }));
  }, [marketDetail, search]);

  const totals = useMemo(() => {
    const groups = marketDetail?.groups || [];
    return groups.reduce((acc, group) => {
      acc.totalStake += Number(group.stake || 0);
      acc.totalBets += Number(group.bets || 0);
      acc.totalActive += Number(group.active || 0);
      if (String(group.session || "").toUpperCase() === "CLOSE") {
        acc.closeStake += Number(group.stake || 0);
        acc.closeBets += Number(group.bets || 0);
      } else {
        acc.openStake += Number(group.stake || 0);
        acc.openBets += Number(group.bets || 0);
      }
      return acc;
    }, {
      totalStake: 0,
      totalBets: 0,
      totalActive: 0,
      openStake: 0,
      openBets: 0,
      closeStake: 0,
      closeBets: 0,
    });
  }, [marketDetail]);

  return (
    <AdminLayout>
      <section style={page}>
        <div style={topRow}>
          <button type="button" style={backButton} onClick={() => navigate("/admin/stake-analysis")}>
            <ArrowLeft size={18} />
          </button>
          <div style={headerWrap}>
            <h1 style={title}>{marketDetail?.market?.name || "Stake Market Detail"}</h1>
            <p style={subtitle}>Stake breakdown by market and session - {selectedDate.split("-").reverse().join("/")}</p>
          </div>
        </div>

        {marketDetail && (
          <>
            <section style={headerCard}>
              <div style={resultLine}>
                <span>Open Result: <b>{marketDetail.results?.open?.result || "***"}</b></span>
                <span>Close Result: <b>{marketDetail.results?.close?.result || "***"}</b></span>
                <span>Total Winners: <b>{marketDetail.results?.total_winners || 0}</b></span>
              </div>
              <div style={actions}>
                <div style={toggleWrap}>
                  {["ALL", "OPEN", "CLOSE"].map((item) => (
                    <button
                      key={item}
                      type="button"
                      style={session === item ? activeToggle : toggleButton}
                      onClick={() => setSession(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div style={searchBox}>
                  <Search size={16} color="#64748b" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search number..."
                    style={searchInput}
                  />
                </div>
                <button type="button" style={exportButton}>
                  <Download size={16} />
                  Export
                </button>
              </div>
            </section>

            <section style={summaryGrid}>
              <article style={{ ...summaryCard, background: "#eff6ff", borderColor: "#bfdbfe" }}>
                <span style={summaryLabel}>TOTAL STAKE</span>
                <strong style={{ ...summaryValue, color: "#1d4ed8" }}>{formatCompactMoney(totals.totalStake)}</strong>
                <small style={summaryMeta}>{`${totals.totalBets} bets placed`}</small>
              </article>
              <article style={{ ...summaryCard, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                <span style={summaryLabel}>OPEN STAKE</span>
                <strong style={{ ...summaryValue, color: "#15803d" }}>{formatCompactMoney(totals.openStake)}</strong>
                <small style={summaryMeta}>{`${totals.openBets} open bets`}</small>
              </article>
              <article style={{ ...summaryCard, background: "#fff1f2", borderColor: "#fecdd3" }}>
                <span style={summaryLabel}>CLOSE STAKE</span>
                <strong style={{ ...summaryValue, color: "#be123c" }}>{formatCompactMoney(totals.closeStake)}</strong>
                <small style={summaryMeta}>{`${totals.closeBets} close bets`}</small>
              </article>
              <article style={{ ...summaryCard, background: "#faf5ff", borderColor: "#e9d5ff" }}>
                <span style={summaryLabel}>ACTIVE NUMBERS</span>
                <strong style={{ ...summaryValue, color: "#7c3aed" }}>{totals.totalActive}</strong>
                <small style={summaryMeta}>{`${filteredGroups.length} stake groups`}</small>
              </article>
            </section>

            {filteredGroups.map((group) => {
              const selectedNumber = group.numbers.find((item) => `${group.key}-${item.number}` === expandedNumberKey);
              return (
              <section key={group.key} style={groupCard}>
                <div style={groupHeader}>
                  <div>
                    <div style={groupTitle}>{group.label}</div>
                    <div style={groupSessionPill(group.session)}>{String(group.session || "OPEN").toUpperCase()} SESSION</div>
                  </div>
                  <div style={groupStats}>
                    <span>Stake: <b style={{ color: "#2563eb" }}>{formatCompactMoney(group.stake)}</b></span>
                    <span>Bets: <b style={{ color: "#16a34a" }}>{group.bets}</b></span>
                    <span>Active: <b>{group.active}{group.total_slots ? `/${group.total_slots}` : ""}</b></span>
                  </div>
                </div>

                <div style={numbersGrid}>
                  {group.numbers.map((item, index) => (
                    <button
                      type="button"
                      key={`${group.key}-${item.number}`}
                      onClick={() => {
                        const key = `${group.key}-${item.number}`;
                        setExpandedNumberKey((current) => current === key ? "" : key);
                      }}
                      style={{
                        ...numberCell,
                        ...(index === 0 ? topCellGold : {}),
                        ...(index === 1 ? topCellGray : {}),
                        ...(index === 2 ? topCellOrange : {}),
                        ...(expandedNumberKey === `${group.key}-${item.number}` ? activeNumberCell : {}),
                      }}
                    >
                      <span style={countBubble}>{item.bets}</span>
                      <strong style={numberLabel}>{item.number}</strong>
                      <span style={stakeLabel}>{formatCompactMoney(item.stake)}</span>
                    </button>
                  ))}
                  {group.numbers.length === 0 && <div style={emptyGroup}>No stake for this group</div>}
                </div>
                {selectedNumber && (
                  <div style={userBreakdownPanel}>
                    <div style={userBreakdownHeader}>
                      <strong>Users on {selectedNumber.number}</strong>
                      <span>{selectedNumber.users?.length || 0} users / {formatCompactMoney(selectedNumber.stake)}</span>
                    </div>
                    <div style={userBreakdownList}>
                      {(selectedNumber.users || []).map((user) => (
                        <div key={`${group.key}-${selectedNumber.number}-${user.user_id}`} style={userBreakdownRow}>
                          <AdminUserLink userId={user.user_id} name={user.user_name || `User ${user.user_id}`} phone={user.user_phone || "-"} />
                          <span>{user.bets} bets</span>
                          <b>{formatCompactMoney(user.stake)}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )})}
          </>
        )}

        {!marketDetail && !!errorMessage && (
          <section style={errorState}>
            <strong style={errorTitle}>Stake detail could not load</strong>
            <span style={errorText}>{errorMessage}</span>
            <button type="button" style={retryButton} onClick={loadDetail}>
              Retry
            </button>
          </section>
        )}
      </section>
    </AdminLayout>
  );
}

function formatCompactMoney(value) {
  const amount = Number(value || 0);
  if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(1)}K`;
  return `Rs ${amount}`;
}

const page = { display: "grid", gap: "18px" };
const topRow = { display: "flex", alignItems: "center", gap: "12px" };
const backButton = { width: "42px", height: "42px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const headerWrap = { flex: 1 };
const title = { margin: 0, fontSize: "18px", fontWeight: 800 };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: "12px" };
const headerCard = { background: "linear-gradient(90deg, #9333ea, #a855f7)", color: "#fff", borderRadius: "18px", padding: "18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", flexWrap: "wrap" };
const resultLine = { display: "flex", gap: "18px", flexWrap: "wrap", fontSize: "13px" };
const actions = { display: "flex", gap: "10px", alignItems: "center" };
const toggleWrap = { display: "flex", gap: "8px", background: "rgba(255,255,255,0.12)", padding: "6px", borderRadius: "999px" };
const toggleButton = { height: "34px", padding: "0 14px", border: 0, borderRadius: "999px", background: "transparent", color: "#fff", cursor: "pointer", fontWeight: 700 };
const activeToggle = { ...toggleButton, background: "#fff", color: "#2563eb" };
const searchBox = { width: "220px", height: "48px", background: "#fff", borderRadius: "0", display: "flex", alignItems: "center", gap: "10px", padding: "0 14px" };
const searchInput = { border: 0, outline: 0, width: "100%", fontSize: "15px" };
const exportButton = { height: "46px", padding: "0 18px", border: 0, borderRadius: "0", background: "rgba(255,255,255,0.15)", color: "#fff", display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, cursor: "pointer" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" };
const summaryCard = { border: "1px solid", borderRadius: "18px", padding: "16px", display: "grid", gap: "8px", background: "#fff" };
const summaryLabel = { fontSize: "11px", fontWeight: 900, color: "#64748b", letterSpacing: ".08em" };
const summaryValue = { fontSize: "24px", fontWeight: 900, color: "#0f172a" };
const summaryMeta = { color: "#64748b", fontSize: "12px", fontWeight: 700 };
const groupCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", overflow: "hidden" };
const groupHeader = { minHeight: "64px", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderBottom: "1px solid #e5e7eb", gap: "12px", flexWrap: "wrap" };
const groupTitle = { fontSize: "16px", fontWeight: 800, color: "#0f172a" };
const groupStats = { display: "flex", gap: "22px", fontSize: "13px", color: "#475569" };
const numbersGrid = { display: "grid", gridTemplateColumns: "repeat(10, minmax(0, 1fr))", gap: "8px", padding: "16px" };
const numberCell = { minHeight: "78px", border: "1px solid #d1d5db", background: "#fff", position: "relative", display: "grid", justifyItems: "center", alignContent: "center", gap: "8px", cursor: "pointer", color: "#0f172a" };
const activeNumberCell = { outline: "3px solid rgba(37,99,235,.28)", borderColor: "#2563eb", transform: "translateY(-1px)" };
const topCellGold = { background: "linear-gradient(180deg, #fbbf24, #f59e0b)", color: "#fff", borderColor: "#f59e0b" };
const topCellGray = { background: "linear-gradient(180deg, #9ca3af, #6b7280)", color: "#fff", borderColor: "#6b7280" };
const topCellOrange = { background: "linear-gradient(180deg, #fb923c, #ef4444)", color: "#fff", borderColor: "#ef4444" };
const countBubble = { position: "absolute", top: "6px", right: "6px", minWidth: "22px", height: "22px", padding: "0 6px", borderRadius: "999px", background: "#dbeafe", color: "#2563eb", fontSize: "12px", fontWeight: 700, display: "grid", placeItems: "center" };
const numberLabel = { fontSize: "18px", fontWeight: 800 };
const stakeLabel = { fontSize: "14px", fontWeight: 800, color: "inherit" };
const emptyGroup = { gridColumn: "1 / -1", padding: "24px", textAlign: "center", color: "#94a3b8" };
const userBreakdownPanel = { margin: "0 16px 16px", border: "1px solid #bfdbfe", borderRadius: "16px", background: "#eff6ff", overflow: "hidden" };
const userBreakdownHeader = { minHeight: "48px", padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", color: "#1d4ed8", borderBottom: "1px solid #bfdbfe", flexWrap: "wrap" };
const userBreakdownList = { display: "grid", gap: "8px", padding: "12px" };
const userBreakdownRow = { display: "grid", gridTemplateColumns: "1fr .5fr .7fr", gap: "10px", alignItems: "center", background: "#fff", border: "1px solid #dbeafe", borderRadius: "12px", padding: "10px 12px", color: "#0f172a" };
const errorState = { minHeight: "180px", borderRadius: "20px", border: "1px solid #fecaca", background: "#fff5f5", display: "grid", placeItems: "center", gap: "8px", padding: "28px", textAlign: "center" };
const errorTitle = { fontSize: "18px", fontWeight: 900, color: "#991b1b" };
const errorText = { fontSize: "13px", fontWeight: 700, color: "#7f1d1d" };
const retryButton = { height: "44px", padding: "0 18px", border: 0, borderRadius: "12px", background: "#dc2626", color: "#fff", fontWeight: 800, cursor: "pointer" };
const groupSessionPill = (session = "OPEN") => ({
  display: "inline-flex",
  alignItems: "center",
  height: "24px",
  marginTop: "6px",
  padding: "0 10px",
  borderRadius: "999px",
  background: String(session).toUpperCase() === "CLOSE" ? "#fff1f2" : "#ecfdf5",
  color: String(session).toUpperCase() === "CLOSE" ? "#be123c" : "#15803d",
  fontSize: "11px",
  fontWeight: 900,
  letterSpacing: ".04em"
});

export default AdminStakeMarketDetail;

function getIndiaDateValue() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
}
